import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link2 } from "lucide-react";
import type { Editor } from "./canvas";
import { safeRichTextHref } from "./rich-text-links";

type RichEditor = NonNullable<ReturnType<Editor["getRichTextEditor"]>>;

/** Capture the editor range before the URL input takes focus. */
export function RichTextLinkControl({
  rich,
  disabled = false,
  onDismiss,
  label,
}: {
  rich: RichEditor | null;
  disabled?: boolean;
  onDismiss?: () => void;
  label?: string;
}) {
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLFormElement>(null);
  const selection = useRef({ from: 0, to: 0, wasLink: false });
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open) return;
    panel.current?.querySelector("input")?.focus();
    const outside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        !trigger.current?.contains(target) &&
        !panel.current?.contains(target)
      )
        setOpen(false);
    };
    window.addEventListener("pointerdown", outside, true);
    return () => window.removeEventListener("pointerdown", outside, true);
  }, [open]);

  const restoreSelection = () => {
    if (!rich || rich.isDestroyed) return null;
    const max = rich.state.doc.content.size;
    const from = Math.min(selection.current.from, max);
    const to = Math.min(selection.current.to, max);
    const chain = rich.chain().focus().setTextSelection({ from, to });
    if (from === to && selection.current.wasLink) chain.extendMarkRange("link");
    return chain;
  };
  const close = () => {
    setOpen(false);
    onDismiss?.();
  };
  const rect = open ? trigger.current?.getBoundingClientRect() : null;
  return (
    <>
      <button
        ref={trigger}
        type="button"
        aria-label="Edit link"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        title="Add or edit link"
        disabled={disabled || !rich}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => {
          if (open) return close();
          if (!rich || rich.isDestroyed) return;
          selection.current = {
            from: rich.state.selection.from,
            to: rich.state.selection.to,
            wasLink: rich.isActive("link"),
          };
          setDraft(String(rich.getAttributes("link").href ?? ""));
          setError("");
          setOpen(true);
        }}
      >
        <Link2 size={16} />
        {label && <span>{label}</span>}
      </button>
      {open &&
        rect &&
        createPortal(
          <form
            ref={panel}
            id={id}
            role="dialog"
            aria-label="Edit text link"
            className="rich-link-popover floating-surface"
            style={{
              left: Math.max(8, Math.min(window.innerWidth - 286, rect.left)),
              top: Math.max(
                8,
                Math.min(window.innerHeight - 160, rect.bottom + 6),
              ),
            }}
            onPointerDown={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === "Escape") {
                event.preventDefault();
                close();
              }
            }}
            onSubmit={(event) => {
              event.preventDefault();
              const href = safeRichTextHref(draft);
              if (!href) {
                setError("Use an http, https or mailto link.");
                return;
              }
              restoreSelection()
                ?.setLink({
                  href,
                  target: "_blank",
                  rel: "noopener noreferrer",
                })
                .run();
              close();
            }}
          >
            <strong className="calque-popover-title">Text link</strong>
            <label htmlFor={`${id}-url`}>Link URL</label>
            <input
              id={`${id}-url`}
              type="text"
              inputMode="url"
              value={draft}
              placeholder="https://example.com"
              spellCheck={false}
              onChange={(event) => {
                setDraft(event.target.value);
                setError("");
              }}
            />
            {error && <small role="alert">{error}</small>}
            <div className="rich-link-actions">
              <button
                className="calque-link-remove"
                type="button"
                disabled={!selection.current.wasLink}
                onClick={() => {
                  restoreSelection()?.unsetLink().run();
                  close();
                }}
              >
                Remove
              </button>
              <button
                className="calque-link-cancel"
                type="button"
                onClick={close}
              >
                Cancel
              </button>
              <button className="calque-link-primary" type="submit">
                Apply
              </button>
            </div>
          </form>,
          document.body,
        )}
    </>
  );
}
